import type { BrandPlanScheduleContent, PostContentType } from 'types'
import type { ContentType } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import type { BucketKey } from 'api/clients/ContentDiscoveryClient'
import type { SupportedMediaType } from 'components/Wizards/schemas/Types'

export type PostType = 'social_post' | 'video' | 'blog_post' | 'email'

export type ContentCategory =
  | 'still_image_post'
  | 'carousel_post'
  | 'feed_video_post'
  | 'short_form_video'
  | 'stories'
  | 'newsletter'
  | 'blog_post'

export interface ContentCategoryConfig {
  postType: PostType
  contentType?: PostContentType
  apiType: ContentType
  label: string
  labelSingular: string
  subtitle: string
}

export const CONTENT_CATEGORY_CONFIG: Record<ContentCategory, ContentCategoryConfig> = {
  still_image_post: {
    postType: 'social_post',
    contentType: 'still_image',
    apiType: 'crosspost_post',
    label: 'Still Images',
    labelSingular: 'Still Image',
    subtitle: 'Single image post for social feeds',
  },
  carousel_post: {
    postType: 'social_post',
    contentType: 'carousel',
    apiType: 'crosspost_post',
    label: 'Carousel Posts',
    labelSingular: 'Carousel Post',
    subtitle: 'Multi-image swipeable post',
  },
  feed_video_post: {
    postType: 'social_post',
    contentType: 'video',
    apiType: 'crosspost_post',
    label: 'Feed Video Posts',
    labelSingular: 'Feed Video Post',
    subtitle: 'Standard video for social feeds',
  },
  short_form_video: {
    postType: 'video',
    apiType: 'video_crosspost_post',
    label: 'Short Form Videos',
    labelSingular: 'Short Form Video',
    subtitle: 'Reels, Shorts, TikTok format',
  },
  stories: {
    postType: 'social_post',
    apiType: 'instagram_story',
    label: 'Stories',
    labelSingular: 'Story',
    subtitle: '24-hour ephemeral content',
  },
  blog_post: {
    postType: 'blog_post',
    apiType: 'blog_post',
    label: 'Blogs',
    labelSingular: 'Blog',
    subtitle: 'Long form written content',
  },
  newsletter: {
    postType: 'email',
    apiType: 'newsletter',
    label: 'Emails',
    labelSingular: 'Email',
    subtitle: 'Email newsletter content',
  },
}

export const CONTENT_CATEGORIES = Object.keys(CONTENT_CATEGORY_CONFIG) as ContentCategory[]

export function getMediaTypesForCategory(category: ContentCategory): SupportedMediaType[] {
  const { postType, contentType } = CONTENT_CATEGORY_CONFIG[category]
  if (postType === 'video' || contentType === 'video') return ['video']
  if (contentType === 'still_image' || postType === 'blog_post' || postType === 'email') return ['image']
  return ['image', 'video']
}

/** Platform feed posts that use `content_type` the same way as `crosspost_post`. */
const FEED_PLATFORM_TYPES = new Set<string>([
  'instagram_post',
  'facebook_post',
  'linkedin_post',
  'twitter_post',
  'twitter_thread',
  'google_my_business_post',
])

/**
 * Maps an existing schedule content row to the content category used in create flows
 * (aligned with {@link CONTENT_CATEGORY_CONFIG} / payload `type` + `content_type`).
 */
export function getContentCategoryForScheduleContent(content: BrandPlanScheduleContent): ContentCategory | null {
  const { type, content_type } = content

  if (type === 'crosspost_post') {
    if (content_type === 'carousel') return 'carousel_post'
    if (content_type === 'video') return 'feed_video_post'
    return 'still_image_post'
  }

  if (type === 'instagram_story' || type === 'facebook_story') {
    return 'stories'
  }

  if (type === 'instagram_reel' || type === 'tiktok_post' || type === 'youtube_short') {
    return 'short_form_video'
  }

  for (const category of CONTENT_CATEGORIES) {
    const cfg = CONTENT_CATEGORY_CONFIG[category]
    if (cfg.apiType !== type) continue
    if (cfg.contentType != null) continue
    return category
  }

  if (FEED_PLATFORM_TYPES.has(type)) {
    if (content_type === 'carousel') return 'carousel_post'
    if (content_type === 'video') return 'feed_video_post'
    return 'still_image_post'
  }

  return null
}

export function getCreateScheduleContentBody(category: ContentCategory): {
  type: ContentType
  content_type: PostContentType | null
  is_crosspost: boolean
} {
  switch (category) {
    case 'still_image_post':
      return { type: 'instagram_post', content_type: 'still_image', is_crosspost: true }
    case 'carousel_post':
      return { type: 'instagram_post', content_type: 'carousel', is_crosspost: true }
    case 'feed_video_post':
      return { type: 'instagram_post', content_type: 'video', is_crosspost: true }
    case 'short_form_video':
      return { type: 'youtube_short', content_type: null, is_crosspost: true }
    case 'stories':
      return { type: 'instagram_story', content_type: null, is_crosspost: true }
    case 'blog_post':
      return { type: 'blog_post', content_type: null, is_crosspost: false }
    case 'newsletter':
      return { type: 'newsletter', content_type: null, is_crosspost: false }
    default:
      throw new Error(`Invalid category: ${category}`)
  }
}

export const BUCKET_KEY_TO_CATEGORY: Record<BucketKey, ContentCategory> = {
  post: 'still_image_post',
  story: 'stories',
  carousel: 'carousel_post',
  video_post: 'feed_video_post',
  email: 'newsletter',
  blog: 'blog_post',
  short_form_video: 'short_form_video',
}

export const CATEGORY_TO_BUCKET_KEY: Record<ContentCategory, BucketKey> = {
  still_image_post: 'post',
  stories: 'story',
  carousel_post: 'carousel',
  feed_video_post: 'video_post',
  newsletter: 'email',
  blog_post: 'blog',
  short_form_video: 'short_form_video',
}

export type ContentTypeCounts = Record<ContentCategory, number>

export const DEFAULT_CONTENT_TYPE_COUNTS: ContentTypeCounts = {
  still_image_post: 1,
  carousel_post: 1,
  feed_video_post: 1,
  short_form_video: 0,
  stories: 0,
  newsletter: 1,
  blog_post: 1,
}

export const getAvailableContentCategories = (isStrictBrandControl: boolean): ContentCategory[] =>
  isStrictBrandControl ? CONTENT_CATEGORIES.filter((category) => category !== 'carousel_post') : CONTENT_CATEGORIES

export const CONTENT_CATEGORY_THUMBNAILS: Record<ContentCategory, string> = {
  still_image_post:
    'https://res.cloudinary.com/almanac/image/upload/v1774537058/content_type_images/still-images_zybtub.png',
  carousel_post:
    'https://res.cloudinary.com/almanac/image/upload/v1774537059/content_type_images/carousel_posts_fb0my1.png',
  feed_video_post:
    'https://res.cloudinary.com/almanac/image/upload/v1774537058/content_type_images/feed_video_posts_jdncvr.png',
  short_form_video:
    'https://res.cloudinary.com/almanac/image/upload/v1774537059/content_type_images/short_form_videos_ialazd.png',
  stories: 'https://res.cloudinary.com/almanac/image/upload/v1774537059/content_type_images/stories_kmtmme.png',
  newsletter: 'https://res.cloudinary.com/almanac/image/upload/v1774537058/content_type_images/emails_ksdcr7.png',
  blog_post: 'https://res.cloudinary.com/almanac/image/upload/v1774537058/content_type_images/blogs_yjcvnw.png',
}
